// src/pages/settings/SitesMap.tsx — vue carte des sites (coordonnées géocodées à l'enregistrement du site, cf. SiteForm)

import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BtnEdit, BtnDelete, WarningBadge } from "../../components/IconButtons";
import { Switch } from "../../components/Switch";
import { WeatherPointModal } from "./WeatherPointModal";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import { geocodeAddress, type LatLon } from "../../api/geoApi";
import { fetchNearestStations, type StationCandidate } from "../../api/meteoFranceApi";
import type { UserRole } from "../../auth/roles";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { siteLatLon, siteHasWeatherStation } from "./helpers";

// Les panneaux de contrôle Leaflet (zoom, attribution) ont un z-index de 1000, au-dessus
// du popup-pane (700) — un popup ouvert près d'un bord peut donc se retrouver caché
// derrière eux. On le repasse au premier plan.
const POPUP_Z_INDEX_STYLE = `
.leaflet-popup-pane { z-index: 1200 !important; }
`;
if (typeof document !== "undefined" && !document.getElementById("sites-map-popup-zindex-css")) {
  const s = document.createElement("style");
  s.id = "sites-map-popup-zindex-css";
  s.textContent = POPUP_Z_INDEX_STYLE;
  document.head.appendChild(s);
}

const FRANCE_CENTER: [number, number] = [46.6, 2.4];

// Pin en losange plutôt que l'icône par défaut de Leaflet — évite les soucis de
// résolution d'assets (marker-icon.png) avec Vite et reste cohérent avec le thème de l'appli.
// Quand plusieurs sites partagent les mêmes coordonnées GPS (un seul pin par emplacement,
// cf. SiteMarker), un badge rond affiche leur nombre — icônes mises en cache par count pour
// éviter de recréer un L.DivIcon identique à chaque rendu.
const pinIconCache = new Map<number, L.DivIcon>();
function getPinIcon(count: number): L.DivIcon {
  let icon = pinIconCache.get(count);
  if (!icon) {
    icon = L.divIcon({
      className: "",
      html: `<div style="position:relative;width:24px;height:24px;">
        <div style="width:24px;height:24px;border-radius:50% 50% 50% 0;background:var(--green-dim);
          transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>
        ${count > 1 ? `<div style="position:absolute;top:-6px;right:-6px;min-width:16px;height:16px;
          box-sizing:border-box;border-radius:50%;background:#fff;border:1.5px solid var(--green-dim);
          color:var(--green-dim);font-size:10px;font-weight:800;font-family:sans-serif;line-height:1;
          display:flex;align-items:center;justify-content:center;padding:0 3px;">${count}</div>` : ""}
      </div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 24],
      popupAnchor: [0, -24],
    });
    pinIconCache.set(count, icon);
  }
  return icon;
}

// Marqueur rond distinct des pins de sites (losange vert) — stations Météo-France, logo officiel.
const stationIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:6px;overflow:hidden;
    border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);">
    <img src="/meteo_france_logo.png" alt="" style="width:100%;height:100%;object-fit:cover;"/>
  </div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11],
});

/** "20251229" → "29/12/2025" */
function formatDateFin(dateFin: number): string {
  const s = String(dateFin);
  return `${s.slice(6,8)}/${s.slice(4,6)}/${s.slice(0,4)}`;
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [32, 32] });
  }, [map, points]);
  return null;
}

// Marqueur + tooltip au survol (Popup Leaflet piloté par mouseover/mouseout plutôt que
// par clic — un vrai Tooltip Leaflet ne peut pas héberger de boutons cliquables).
// Un petit délai à la fermeture laisse le temps à la souris de rejoindre le contenu du
// tooltip pour cliquer Modifier/Supprimer sans qu'il se referme avant.
// `sites` regroupe tous les sites partageant exactement les mêmes coordonnées GPS (un seul
// marqueur ne peut afficher qu'un pin à la fois) — le popup liste alors une fiche par site.
function SiteMarker({ sites, coord, clients, onEdit, onDelete, onShowWeather }: {
  sites: SiteRecord[]; coord: LatLon; clients: ClientRecord[];
  onEdit: (s: SiteRecord) => void; onDelete: (s: SiteRecord) => void;
  onShowWeather: (site: SiteRecord, coord: LatLon) => void;
}) {
  const markerRef  = useRef<L.Marker>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const [activeIdx, setActiveIdx] = useState(0);

  const open = () => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = undefined; }
    markerRef.current?.openPopup();
  };
  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => markerRef.current?.closePopup(), 150);
  };

  const idx    = Math.min(activeIdx, sites.length - 1);
  const site   = sites[idx];
  const photo  = site.data.coverProps?.photoUrl;
  const client = clients.find(cl => cl.id === site.clientId);

  return (
    <Marker ref={markerRef} position={[coord.lat, coord.lon]} icon={getPinIcon(sites.length)}
      eventHandlers={{ mouseover: open, mouseout: scheduleClose }}>
      <Popup autoClose={false} closeButton={false} closeOnClick={false}>
        <div onMouseEnter={open} onMouseLeave={scheduleClose}
          style={{fontFamily:"var(--ff-title)",minWidth:220,maxWidth:280}}>
          {sites.length > 1 && (
            <div style={{display:"flex",gap:4,marginBottom:10,flexWrap:"wrap"}}>
              {sites.map((s, i) => (
                <button key={s.id} onClick={()=>setActiveIdx(i)} title={s.name}
                  style={{width:22,height:22,borderRadius:"50%",cursor:"pointer",padding:0,
                    fontSize:11,fontWeight:700,fontFamily:"var(--ff-title)",
                    border:i===idx?"1.5px solid var(--green-dim)":"1px solid #ddd",
                    background:i===idx?"var(--green-dim)":"#fff",
                    color:i===idx?"#fff":"#666",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {i+1}
                </button>
              ))}
            </div>
          )}
          <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:8}}>
            {photo
              ? <img src={photo} alt="" style={{width:48,height:36,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
              : <div style={{width:48,height:36,borderRadius:6,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>🏗</div>}
            <div style={{minWidth:0}}>
              <div style={{fontWeight:700,fontSize:13,color:"#222"}}>{site.name}</div>
              <div style={{fontSize:11,color:"#888"}}>{client?.name ?? site.clientId}</div>
            </div>
          </div>
          <div style={{fontSize:11,color:"#888",marginBottom:8}}>
            {site.pdls.length>0 && <>⚡ {site.pdls.length} PDL</>}
            {site.pdls.length>0 && site.pces.length>0 && <> · </>}
            {site.pces.length>0 && <>🔥 {site.pces.length} PCE</>}
            {site.pdls.length===0 && site.pces.length===0 && <>aucun compteur</>}
          </div>
          {site.data.description && (
            <div style={{fontSize:11,color:"#aaa",marginBottom:8}}>{site.data.description}</div>
          )}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:6,borderTop:"1px solid #eee"}}>
            <div style={{position:"relative",flexShrink:0}}>
              <button onClick={()=>onShowWeather(site, coord)}
                title={siteHasWeatherStation(site) ? "Températures & DJU" : "Aucune station Météo-France rattachée — les blocs DJU du rapport (rigueur climatique, prévision) échoueront"}
                style={{background:"none",border:"1.5px solid #ddd",borderRadius:8,
                  width:36,height:36,cursor:"pointer",fontSize:16,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                🌡
              </button>
              {!siteHasWeatherStation(site) && <WarningBadge title="Aucune station Météo-France rattachée"/>}
            </div>
            <BtnEdit onClick={()=>onEdit(site)} title="Modifier le site"/>
            <BtnDelete onClick={()=>onDelete(site)} title="Supprimer le site"/>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

// Marqueur d'une station Météo-France effectivement rattachée à un ou plusieurs sites (cf.
// loadStationMarkers ci-dessous) — nom, coordonnées, fraîcheur des données, lien vers sa fiche
// info sur le web, puis liste des sites rattachés avec distance.
function WeatherStationMarker({ station, sites }: {
  station: StationCandidate; sites: { name: string; distanceKm: number }[];
}) {
  return (
    <Marker position={[station.lat, station.lon]} icon={stationIcon}>
      <Popup>
        <div style={{fontFamily:"var(--ff-title)",fontSize:12,minWidth:180}}>
          <div style={{display:"flex",alignItems:"center",gap:6,fontWeight:700,marginBottom:2}}>
            <img src="/meteo_france_logo.png" alt="" style={{width:16,height:16,borderRadius:4,objectFit:"cover",flexShrink:0}}/>
            {station.nom}
          </div>
          <div style={{color:"#999",fontSize:11,marginBottom:2}}>{station.lat.toFixed(4)}, {station.lon.toFixed(4)}</div>
          <div style={{color:"#999",fontSize:11,marginBottom:4}}>Données au {formatDateFin(station.dateFin)}</div>
          <a href={`https://www.infoclimat.fr/stations-meteo/?s=${station.id}`}
            target="_blank" rel="noopener noreferrer"
            title="Fiche de la station sur Infoclimat.fr (peut être indisponible pour les stations climatologiques sans télémesure temps réel)"
            style={{display:"inline-block",fontSize:11,color:"var(--green-dim,#5a9)",textDecoration:"none",marginBottom:6}}>
            ℹ️ Fiche station
          </a>
          <div style={{fontSize:11,color:"#666",display:"flex",flexDirection:"column",gap:2}}>
            {sites.map(ns=>(
              <div key={ns.name}>{ns.name} — {ns.distanceKm} km</div>
            ))}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

export function SitesMap({ sites, clients, onEdit, onDelete, onSaveSite, onSave, userRole, setModal, showToast }: {
  sites: SiteRecord[]; clients: ClientRecord[];
  onEdit: (s: SiteRecord) => void; onDelete: (s: SiteRecord) => void;
  onSaveSite: (s: SiteRecord) => Promise<void>;
  /** Sauvegarde "silencieuse" (sans toast individuel) — utilisée par la géolocalisation en lot. */
  onSave: (s: SiteRecord) => Promise<void>;
  userRole?: UserRole;
  setModal: (m: ConfirmModalProps | null) => void;
  showToast: (msg: string) => void;
}) {
  // Modale "Températures & DJU" — gérée ici (et non dans SiteMarker) car les popups Leaflet
  // vivent dans un conteneur transformé par la carte : une modale position:fixed rendue à
  // l'intérieur y serait positionnée relativement à ce conteneur, pas au viewport.
  const [weatherPoint, setWeatherPoint] = useState<{ site: SiteRecord; coord: LatLon } | null>(null);
  const [bulkRunning,  setBulkRunning]  = useState(false);

  // Garde la fiche site du popup à jour après un enregistrement (choix de source météo),
  // pour que la modale recalcule correctement son état "modifications non enregistrées".
  const handleSaveSite = async (updated: SiteRecord) => {
    await onSaveSite(updated);
    setWeatherPoint(wp => wp ? { ...wp, site: updated } : wp);
  };

  const located: [SiteRecord, LatLon][] = sites
    .map(s => [s, siteLatLon(s)] as const)
    .filter((x): x is [SiteRecord, LatLon] => !!x[1]);
  const unlocated = sites.filter(s => !siteLatLon(s));

  // Plusieurs sites peuvent partager exactement les mêmes coordonnées GPS (un seul pin ne
  // peut pas se dédoubler visuellement) — on les regroupe par coordonnée pour n'afficher
  // qu'un marqueur, dont le popup liste alors une fiche par site.
  const locatedGroups = new Map<string, { coord: LatLon; sites: SiteRecord[] }>();
  for (const [s, c] of located) {
    const key = `${c.lat},${c.lon}`;
    const g = locatedGroups.get(key) ?? { coord: c, sites: [] };
    g.sites.push(s);
    locatedGroups.set(key, g);
  }
  const points: [number, number][] = [...locatedGroups.values()].map(g => [g.coord.lat, g.coord.lon] as [number, number]);

  const canBulkGeolocate = userRole === "admin" || userRole === "super-admin";

  // Marqueur de la station Météo-France effectivement rattachée à chaque site géolocalisé (celle
  // choisie via WeatherPointModal, coverProps.meteoFranceStationId) — pas les stations candidates
  // proches. Affichage à la demande (bouton), pas au chargement de la carte, pour éviter N
  // requêtes systématiques. Sites sans station rattachée : ignorés (rien à afficher pour eux).
  const [showStations,     setShowStations]     = useState(false);
  const [stationsLoading,  setStationsLoading]   = useState(false);
  const [stationMarkers,   setStationMarkers]    = useState<{ station: StationCandidate; sites: { name: string; distanceKm: number }[] }[]>([]);

  const loadStationMarkers = async () => {
    setStationsLoading(true);
    try {
      const year = new Date().getFullYear();
      const withStation = located.filter(([site]) => siteHasWeatherStation(site));
      const perSite = await Promise.all(withStation.map(async ([site, coord]) => {
        const stationId = parseInt(site.data.coverProps?.meteoFranceStationId ?? "", 10);
        // fetchNearestStations réutilisée telle quelle (pas de nouvel appel serveur) — on ne
        // garde que le candidat correspondant à la station réellement rattachée au site.
        const { candidates } = await fetchNearestStations(coord.lat, coord.lon, year);
        const attached = candidates.find(c => c.id === stationId) ?? null;
        return { site, attached };
      }));
      const byId = new Map<number, { station: StationCandidate; sites: { name: string; distanceKm: number }[] }>();
      for (const { site, attached } of perSite) {
        if (!attached) continue;
        const entry = byId.get(attached.id) ?? { station: attached, sites: [] };
        entry.sites.push({ name: site.name, distanceKm: attached.distanceKm });
        byId.set(attached.id, entry);
      }
      setStationMarkers([...byId.values()]);
    } catch (e) {
      showToast(`Stations indisponibles : ${(e as Error).message}`);
      setShowStations(false);
    } finally {
      setStationsLoading(false);
    }
  };

  const toggleStations = () => {
    if (showStations) { setShowStations(false); return; }
    setShowStations(true);
    loadStationMarkers();
  };

  // Géocode chaque site sans coordonnées puis lui assigne la station Météo-France la plus
  // proche (ou la plus proche tout court si aucune n'est complète pour l'année en cours).
  const runBulkGeolocate = async () => {
    const year = new Date().getFullYear();
    setBulkRunning(true);
    let okCount = 0;
    const failed: string[] = [];
    try {
      for (const site of unlocated) {
        const commune    = site.data.coverProps?.commune ?? "";
        const codePostal = site.data.coverProps?.codePostal ?? "";
        if (!commune && !codePostal) { failed.push(`${site.name} (adresse incomplète)`); continue; }
        try {
          const { lat, lon } = await geocodeAddress(commune, codePostal);
          let meteoFranceStationId: string | undefined;
          try {
            const { recommended, candidates } = await fetchNearestStations(lat, lon, year);
            const stationId = recommended ?? candidates[0]?.id ?? null;
            if (stationId != null) meteoFranceStationId = String(stationId);
          } catch { /* station facultative — le géocodage seul suffit à localiser le site sur la carte */ }
          const coverProps = { ...site.data.coverProps, lat: String(lat), lon: String(lon),
            ...(meteoFranceStationId ? { meteoFranceStationId } : {}) };
          await onSave({ ...site, data: { ...site.data, coverProps } });
          okCount++;
        } catch (e) {
          failed.push(`${site.name} (${(e as Error).message})`);
        }
      }
    } finally {
      setBulkRunning(false);
      setModal(null);
      showToast(
        failed.length === 0
          ? `${okCount} site${okCount>1?"s":""} géolocalisé${okCount>1?"s":""} ✓`
          : `${okCount} site${okCount>1?"s":""} géolocalisé${okCount>1?"s":""}, ${failed.length} échec${failed.length>1?"s":""} : ${failed.join(", ")}`,
      );
    }
  };

  const confirmBulkGeolocate = () => {
    setModal({
      title: "Géolocaliser les sites manquants ?",
      message: `${unlocated.length} site${unlocated.length>1?"s":""} sans coordonnées. Tentative de géocodage de l'adresse puis d'attribution de la station Météo-France la plus proche pour chacun.`,
      confirmLabel: "Géolocaliser",
      onConfirm: runBulkGeolocate,
      onCancel: () => setModal(null),
    });
  };

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column"}}>
      <div style={{flex:1,minHeight:0,borderRadius:12,overflow:"hidden",border:"1px solid var(--border-light)",position:"relative"}}>
        {located.length > 0 && (
          <div title="Affiche la station Météo-France rattachée à chaque site géolocalisé"
            style={{position:"absolute",top:10,right:10,zIndex:1300,
              display:"flex",alignItems:"center",gap:8,
              background:"#fff",border:"1px solid #ddd",borderRadius:8,
              padding:"6px 10px",boxShadow:"0 2px 8px rgba(0,0,0,0.15)",
              opacity:stationsLoading?0.7:1}}>
            <span style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontFamily:"var(--ff-title)",fontWeight:700,color:"var(--text-primary)"}}>
              Stations
              <img src="/meteo_france_logo.png" alt="Météo-France" style={{width:16,height:16,borderRadius:4,objectFit:"cover"}}/>
            </span>
            <Switch checked={showStations} onChange={toggleStations} disabled={stationsLoading}/>
            <span style={{fontSize:11,color:"var(--text-muted)",minWidth:52}}>
              {stationsLoading ? "Recherche…" : showStations ? "Masquer" : "Afficher"}
            </span>
          </div>
        )}
        <MapContainer center={FRANCE_CENTER} zoom={5.5} style={{height:"100%",width:"100%"}} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points}/>
          {[...locatedGroups.entries()].map(([key, g]) => (
            <SiteMarker key={key} sites={g.sites} coord={g.coord} clients={clients}
              onEdit={onEdit} onDelete={onDelete}
              onShowWeather={(site, coord)=>setWeatherPoint({site, coord})}/>
          ))}
          {showStations && stationMarkers.map(({station, sites: nearSites})=>(
            <WeatherStationMarker key={`station-${station.id}`} station={station} sites={nearSites}/>
          ))}
        </MapContainer>
      </div>
      {weatherPoint && (
        <WeatherPointModal site={weatherPoint.site} lat={weatherPoint.coord.lat} lon={weatherPoint.coord.lon}
          onClose={()=>setWeatherPoint(null)} onSaveSite={handleSaveSite}/>
      )}
      {unlocated.length > 0 && (
        <div style={{flexShrink:0,marginTop:12,padding:"10px 14px",background:"#fffbea",
          border:"1px solid #ffe082",borderRadius:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <svg width="19" height="17" viewBox="0 0 18 16" fill="none" style={{flexShrink:0}}>
              <path d="M9 1.5 L17 14.5 H1 Z" stroke="var(--gold)" strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
              <line x1="9" y1="6" x2="9" y2="10" stroke="var(--gold)" strokeWidth="1.6" strokeLinecap="round"/>
              <circle cx="9" cy="12.3" r="0.9" fill="var(--gold)"/>
            </svg>
            <div style={{fontSize:12,color:"#7a5c00",flex:1}}>
              <strong>{unlocated.length} site{unlocated.length>1?"s":""} non localisé{unlocated.length>1?"s":""}</strong>
              {" "}— coordonnées non enregistrées, non affiché{unlocated.length>1?"s":""} sur la carte. Modifiez le site (adresse) et enregistrez pour le géocoder :
            </div>
            {canBulkGeolocate && (
              <button onClick={confirmBulkGeolocate} disabled={bulkRunning}
                title="Géocode l'adresse de chaque site sans coordonnées, puis lui assigne la station Météo-France la plus proche"
                style={{flexShrink:0,background:"#fff3cd",border:"1px solid #ffe082",borderRadius:6,cursor:bulkRunning?"not-allowed":"pointer",
                  padding:"5px 10px",fontSize:11,fontFamily:"var(--ff-title)",fontWeight:700,color:"#7a5c00",opacity:bulkRunning?0.6:1}}>
                {bulkRunning ? "Géolocalisation…" : "🛰 Géolocaliser tout"}
              </button>
            )}
          </div>
          <div style={{marginTop:6,marginLeft:29,display:"flex",flexWrap:"wrap",gap:6}}>
            {unlocated.map(s=>(
              <button key={s.id} onClick={()=>onEdit(s)} title="Modifier le site"
                style={{background:"#fff3cd",border:"1px solid #ffe082",borderRadius:20,cursor:"pointer",
                  padding:"2px 10px",fontSize:11,fontFamily:"var(--ff-title)",fontWeight:600,color:"#7a5c00"}}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
